import React from "react";
import { LucideIcon, HelpCircle, TrendingUp, TrendingDown } from "lucide-react";
import Tooltip from "../overlays/Tooltip";
import { formatNumber } from "../../utils/formatNumber";
import { usePartnerStore } from "../../store/partnerStore";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  toolTipText?: string;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  className?: string;
  currency?: string;
  isMonetary?: boolean;
  dark?: boolean;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon: Icon,
  toolTipText,
  trend,
  className = "",
  currency,
  dark = false,
  isMonetary = false,
}) => {
  const { partnerConfig } = usePartnerStore();

  // Split value and currency if they're combined in the value prop
  const displayValue =
    typeof value === "string" && currency
      ? value.replace(currency, "").trim()
      : value;

  // Ensure we have a valid number for formatting
  const numericValue =
    typeof displayValue === "number" && !isNaN(displayValue)
      ? displayValue
      : typeof displayValue === "string"
      ? parseFloat(displayValue) || 0
      : 0;

  return (
    <div
      className={`${
        dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
      } rounded-xl p-6 shadow-sm border ${className}`}
    >
      <div className="flex justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p
              className={`text-sm ${dark ? "text-gray-300" : "text-gray-500"}`}
            >
              {title}
            </p>
            {toolTipText && (
              <Tooltip text={toolTipText} width="max-w-80" delayDuration={100}>
                <HelpCircle
                  className={`w-4 h-4 ${
                    dark
                      ? "text-gray-400 hover:text-gray-200"
                      : "text-gray-400 hover:text-vGray800"
                  } cursor-help`}
                />
              </Tooltip>
            )}
          </div>
          <h3
            className={`text-3xl font-semibold mt-1 ${
              dark ? "text-white" : "text-gray-900"
            }`}
          >
            {formatNumber(numericValue)}

            {isMonetary && (
              <span
                className={`text-lg ml-1 ${
                  dark ? "text-gray-300" : "text-gray-500"
                }`}
              >
                {partnerConfig?.defaultCurrency}
              </span>
            )}
          </h3>
        </div>
        <div>
          <Icon
            className={`w-6 h-6 ${
              dark
                ? "text-gray-300 hover:text-white"
                : "text-vGray700 hover:text-vGray800"
            }`}
          />
        </div>
      </div>
      {trend && (
        <div
          className={`mt-4 flex items-center ${
            trend.isPositive ? "text-green-600" : "text-gray-600"
          }`}
        >
          {trend.isPositive ? (
            <TrendingUp className="w-4 h-4 mr-1" />
          ) : (
            <TrendingDown className="w-4 h-4 mr-1" />
          )}
          <span className="text-sm font-medium">{trend.value}</span>
        </div>
      )}
    </div>
  );
};
